import React, { useState } from "react";
import { cn } from "@/lib/utils";
import { NavGroup } from "./NavGroup";
import { NavItem } from "./NavItem";
import { ThemeToggle } from "@/components/ThemeToggle";
import { FundSelector } from "../../funds/FundSelector/FundSelector";
import { ClientAvatar } from "@/features/client/components/ClientAvatar";
import { GettingStartedTrigger } from "@/features/getting-started-guide/GettingStartedTrigger";
import { ChevronRight } from "lucide-react";

interface SidebarContentProps {
  isCollapsed: boolean;
  toggleSidebar: () => void;
  navigate: (path: string) => void;
  navigationGroups: {
    label: string;
    icon?: React.ElementType;
    disabled?: boolean;
    disabledMessage?: string;
    collapsed?: boolean;
    items: {
      icon: React.ComponentType;
      label: string;
      path?: string;
      external?: boolean;
      url?: string;
      disabled?: boolean;
      context?: string;
      subItems?: {
        icon: React.ComponentType;
        label: string;
        path?: string;
        external?: boolean;
        url?: string;
        disabled?: boolean;
        context?: string;
      }[];
    }[];
  }[];
  isMobile?: boolean;
  onToggleGroupCollapse?: (groupLabel: string) => void;
}

export const SidebarContent: React.FC<SidebarContentProps> = ({
  isCollapsed,
  toggleSidebar,
  navigate,
  navigationGroups,
  isMobile = false,
  onToggleGroupCollapse,
}) => {
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>(
    {}
  );

  const toggleExpanded = (groupIndex: number, itemIndex: number) => {
    const key = `${groupIndex}-${itemIndex}`;
    setExpandedItems((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Check if a group is expanded by looking at any of its items
  const isGroupExpanded = (groupIndex: number): boolean => {
    return Object.keys(expandedItems).some((key) => {
      const [gIndex, _] = key.split("-");
      return gIndex === groupIndex.toString() && expandedItems[key];
    });
  };

  const handleToggleGroupCollapse = (groupLabel: string) => {
    if (onToggleGroupCollapse) {
      onToggleGroupCollapse(groupLabel);
    }
  };

  return (
    <div
      className={cn(
        "h-full flex flex-col justify-between relative",
        "bg-background/80 backdrop-blur-sm border-r border-border",
        isCollapsed ? "px-2" : "px-4"
      )}
    >
      <div
        className={cn(
          "flex-1 overflow-y-auto pt-4 h-full w-full",
          "flex flex-col gap-6",
          isCollapsed ? "items-center" : "items-start"
        )}
      >
        <div className="w-full space-y-6">
          {navigationGroups.map((group, groupIndex) => (
            <React.Fragment key={groupIndex}>
              <NavGroup
                label={group.label}
                icon={group.icon}
                isCollapsed={isCollapsed}
                collapsed={group.collapsed}
                disabled={group.disabled}
                disabledMessage={group.disabledMessage}
                onToggleCollapse={() => handleToggleGroupCollapse(group.label)}
              >
                {/* Fund selector under Fund header */}
                {!isCollapsed && group.label === "Fund" && <FundSelector />}

                {group.items.map((item, itemIndex) => {
                  const active = window.location.pathname === item.path;
                  const hasSubItems = !!(
                    item.subItems && item.subItems.length > 0
                  );
                  const isExpanded =
                    expandedItems[`${groupIndex}-${itemIndex}`];
                  // An item is disabled if it has its own disabled flag OR if the parent group is disabled
                  const isDisabled = item.disabled || group.disabled;

                  const handleClick = () => {
                    if (isDisabled) return;

                    if (hasSubItems) {
                      toggleExpanded(groupIndex, itemIndex);
                      return;
                    }

                    if (item.external && item.url) {
                      window.open(item.url, "_blank");
                    } else if (item.path) {
                      navigate(item.path + "?context=" + item.context);
                    }
                  };

                  return (
                    <React.Fragment key={itemIndex}>
                      <NavItem
                        active={active}
                        icon={
                          <div className="h-3.5 w-3.5 flex items-center justify-center">
                            <item.icon />
                          </div>
                        }
                        isExternal={item.external}
                        label={isCollapsed ? "" : item.label}
                        onClick={handleClick}
                        isCollapsed={isCollapsed}
                        hasChildren={hasSubItems}
                        isExpanded={isExpanded}
                        disabled={isDisabled}
                      />

                      {!isCollapsed &&
                        hasSubItems &&
                        isExpanded &&
                        item.subItems && (
                          <div className="ml-6 border-l pl-2 border-border">
                            {item.subItems.map((subItem, subIndex) => {
                              const subActive =
                                window.location.pathname === subItem.path;
                              // A subitem is disabled if it has its own disabled flag OR if the parent item is disabled
                              const subItemDisabled =
                                subItem.disabled || isDisabled;

                              return (
                                <NavItem
                                  key={`${itemIndex}-${subIndex}`}
                                  active={subActive}
                                  icon={
                                    <div className="flex items-center justify-center h-3.5 w-3.5">
                                      <ChevronRight className="h-3 w-3 text-muted-foreground" />
                                    </div>
                                  }
                                  label={subItem.label}
                                  onClick={() => {
                                    if (!subItemDisabled) {
                                      navigate(
                                        subItem.path +
                                          "?context=" +
                                          subItem.context
                                      );
                                    }
                                  }}
                                  isCollapsed={false}
                                  subtle={true}
                                  disabled={subItemDisabled}
                                />
                              );
                            })}
                          </div>
                        )}
                    </React.Fragment>
                  );
                })}
              </NavGroup>

              {/* For collapsed sidebar, show selectors after their respective groups */}
              {isCollapsed && group.label === "Client" && (
                <div className="flex justify-center mb-4 mt-2">
                  <ClientAvatar />
                </div>
              )}
              {isCollapsed && group.label === "Fund" && (
                <div className="flex justify-center mb-4 mt-2">
                  <FundSelector />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div
        className={cn(
          "py-4 border-t border-border mt-auto",
          "flex items-center gap-2",
          isCollapsed ? "justify-center flex-col" : "justify-between"
        )}
      >
        {!isCollapsed && (
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <GettingStartedTrigger 
              variant="ghost" 
              size="icon"
              tooltipText="Show setup guide"
            />
          </div>
        )}
        
        {isCollapsed && (
          <div className="flex flex-col items-center gap-2">
            <ThemeToggle />
            <GettingStartedTrigger 
              variant="ghost" 
              size="icon"
              tooltipText="Show setup guide"
            />
          </div>
        )}

        {/* {!isCollapsed && !isMobile && (
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleSidebar}
            className="h-8 w-8"
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="sr-only">Collapse sidebar</span>
          </Button>
        )}

        {isCollapsed && !isMobile && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggleSidebar}
                  className="h-8 w-8"
                >
                  <ChevronRight className="h-4 w-4" />
                  <span className="sr-only">Expand sidebar</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>Expand sidebar</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )} */}
      </div>
    </div>
  );
};
